import { storeToRefs } from 'pinia'
import { getIsNotFoundApiError } from '@/composables/api-client'
import { useAccountStore } from '@/stores/account'
import { useSettingsStore } from '@/stores/common/settings'
import { useFantasyCalendarStore } from '@/stores/fantasy/calendar'
import { useFantasyDayStore } from '@/stores/fantasy/day'
import { useFantasySeasonStore } from '@/stores/fantasy/season'
import { useFantasyShopSetListStore } from '@/stores/fantasy/shop-set-list'
import {
  mapCalendarDayStatus,
  useFantasyAnalytics
} from '~/composables/fantasy'
import type { FantasyShopSet } from '~/types/fantasy'

export const useFantasyPageInit = () => {
  const { $snacks } = useNuxtApp()
  const { t } = useI18n()
  const route = useRoute()
  const router = useRouter()

  const settingsStore = useSettingsStore()
  const fantasySeasonStore = useFantasySeasonStore()
  const fantasyCalendarStore = useFantasyCalendarStore()
  const fantasyDayStore = useFantasyDayStore()
  const fantasyShopSetListStore = useFantasyShopSetListStore()
  const accountStore = useAccountStore()
  const { trackGameDayScreenView } = useFantasyAnalytics()

  const { isAuthorized } = storeToRefs(accountStore)
  const { items: calendarDays } = storeToRefs(fantasyCalendarStore)
  const { isCompleted } = storeToRefs(fantasySeasonStore)

  const isError = ref(false)
  const isNotFound = ref(false)
  const isDayLoading = ref(false)
  const isInitialized = ref(false)

  const getSeasonId = () => Number(route.params.id)

  const resolveDayId = () =>
    route.query.day
      ? Number(route.query.day)
      : fantasyCalendarStore.defaultDayId

  const fetchPageData = () => {
    const seasonId = getSeasonId()

    return Promise.all([
      settingsStore.fetchData(),
      fantasySeasonStore.fetchData(seasonId),
      fantasyCalendarStore.fetchItems({}, { seasonId })
    ])
      .then(async () => {
        isDayLoading.value = true

        const requests: Promise<unknown>[] = [
          fantasyDayStore.selectDay(resolveDayId())
        ]

        if (!fantasySeasonStore.isCompleted) {
          requests.push(fantasyShopSetListStore.fetchItems())
        }

        try {
          await Promise.all(requests)
        } finally {
          isDayLoading.value = false
        }
      })
      .then(() => {
        isInitialized.value = true
      })
      .catch((error: unknown) => {
        if (getIsNotFoundApiError(error)) {
          isNotFound.value = true
          return
        }

        isError.value = true
        $snacks.error(t('error'))
      })
  }

  const shouldShowFreeCardsBanner = computed(
    () =>
      fantasySeasonStore.canClaimFreeCards &&
      isInitialized.value &&
      !isCompleted.value
  )

  const isCalendarLoading = computed(
    () => isDayLoading.value && !calendarDays.value.length
  )

  const isProfileLoading = computed(
    () => isDayLoading.value && !fantasySeasonStore.data?.memberStatus
  )

  const handleSelectDay = async (id: number) => {
    if (fantasyDayStore.selectedDayId === id) return

    router.replace({
      query: { ...route.query, day: id }
    })

    isDayLoading.value = true

    try {
      await fantasyDayStore.selectDay(id)
    } finally {
      isDayLoading.value = false
    }
  }

  const refreshUserData = async () => {
    isDayLoading.value = true
    const seasonId = getSeasonId()

    try {
      await Promise.all([
        fantasySeasonStore.fetchData(seasonId),
        fantasyCalendarStore.fetchItems({}, { seasonId })
      ])

      const dayId = fantasyDayStore.selectedDayId ?? resolveDayId()
      if (dayId) {
        await fantasyDayStore.fetchItem(dayId)
      }
    } finally {
      isDayLoading.value = false
    }
  }

  watch(isAuthorized, () => {
    if (!isInitialized.value) return
    refreshUserData()
  })

  const trackCurrentGameDayScreenView = () => {
    const dayId = fantasyDayStore.selectedDayId
    if (!dayId) return

    const day = calendarDays.value.find(item => item.id === dayId)
    if (!day) return

    trackGameDayScreenView({
      dayDate: day.date,
      dayStatus: mapCalendarDayStatus({
        can_set_slot: day.can_set_slot,
        is_completed: day.is_completed ?? false
      })
    })
  }

  watch(isInitialized, initialized => {
    if (initialized) {
      trackCurrentGameDayScreenView()
    }
  })

  watch(
    () => fantasyDayStore.selectedDayId,
    (dayId, oldDayId) => {
      if (!isInitialized.value || !dayId || dayId === oldDayId) return
      trackCurrentGameDayScreenView()
    }
  )

  return {
    isError,
    isNotFound,
    isCompleted,
    isDayLoading,
    isCalendarLoading,
    isProfileLoading,
    isAuthorized,
    shouldShowFreeCardsBanner,
    handleSelectDay,
    fetchPageData
  }
}
